import React, { useMemo } from "react";
import { Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faSliders } from "@fortawesome/free-solid-svg-icons";
import { User } from "../../interfaces/User";
import "./CompanyFilters.scss";

interface CompanyFiltersProps {
  // Filter values
  searchQuery: string;
  countryFilter: string;

  // Change handlers
  onSearchChange: (value: string) => void;
  onCountryChange: (value: string) => void;
  onClearFilters: () => void;

  // Items to extract countries from
  allCompanyItems: User[];

  // Optional: Collapse state
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

// Extract unique countries from companies
const extractUniqueCountries = (items: User[]): string[] => {
  const countries = new Set<string>();
  items.forEach((company) => {
    if (company.country && company.country.trim()) {
      countries.add(company.country.trim());
    }
  });
  return Array.from(countries).sort();
};

export function CompanyFilters({
  searchQuery,
  countryFilter,
  onSearchChange,
  onCountryChange,
  onClearFilters,
  allCompanyItems,
  collapsed = false,
  onToggleCollapse,
}: CompanyFiltersProps) {
  // Filter companies by search query before extracting countries
  const filteredItems = useMemo(() => {
    if (!allCompanyItems || allCompanyItems.length === 0) {
      return [];
    }
    // If no search query, return all items
    if (!searchQuery || !searchQuery.trim()) {
      return allCompanyItems;
    }
    // Filter by keyword (matching companyName, firstName, lastName, services, website)
    const keyword = searchQuery.trim().toLowerCase();
    return allCompanyItems.filter((company: User) => {
      return (
        (company.companyName && company.companyName.toLowerCase().includes(keyword)) ||
        (company.firstName && company.firstName.toLowerCase().includes(keyword)) ||
        (company.lastName && company.lastName.toLowerCase().includes(keyword)) ||
        (company.services && company.services.toLowerCase().includes(keyword)) ||
        (company.website && company.website.toLowerCase().includes(keyword))
      );
    });
  }, [allCompanyItems, searchQuery]);

  // Extract unique countries from filtered companies
  const uniqueCountries = useMemo(
    () => extractUniqueCountries(filteredItems),
    [filteredItems]
  );
  return (
    <div className="company-filters">
      <div
        className={`${
          collapsed
            ? "d-flex justify-content-center"
            : "d-flex justify-content-between align-items-center"
        } mb-3`}
      >
        {!collapsed && <h5 className="m-0 filter-title">Filters</h5>}
        {onToggleCollapse && (
          <button
            className={`btn-figma-1 btn-sm filter-collapse-btn ${collapsed ? "filter-collapse-btn--collapsed" : ""}`}
            onClick={onToggleCollapse}
            aria-label={collapsed ? "Expand filters" : "Collapse filters"}
          >
            {collapsed ? <FontAwesomeIcon icon={faSliders} /> : <FontAwesomeIcon icon={faTimes} />}
          </button>
        )}
      </div>

      {/* Filter content - only show when not collapsed */}
      <div className={`filter-content ${collapsed ? "d-none" : ""}`}>
        {!collapsed && (
          <>
            {/* Search Input - Keyword */}
            <Form.Group className="mb-3 filter-first-group">
              <Form.Label htmlFor="search" className="filter-label">
                keyword
              </Form.Label>
              <Form.Control
                type="text"
                id="search"
                className="filter-input"
                placeholder="Input"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </Form.Group>

            {/* Country Filter */}
            <Form.Group className="mb-3">
              <Form.Label htmlFor="country" className="filter-label">
                Country
              </Form.Label>
              <Form.Select
                id="country"
                className="filter-input"
                value={countryFilter}
                onChange={(e) => onCountryChange(e.target.value)}
              >
                <option value="">All</option>
                {uniqueCountries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Clear filters */}
            <button className="btn-cancel-figma w-100" onClick={onClearFilters}>
              Clear All Filters
            </button>
          </>
        )}
      </div>
    </div>
  );
}

